import {
  CurecallButton,
  CurecallConfirmationModal,
  CurecallSearchInput,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
  ECurecallSize,
} from '@honestica/curecall-ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { ErrorQueryHandler } from 'src/components/error/error-query-handler.component'
import { HRContactService } from 'src/services/hr-contact.service'
import { THRContact } from 'src/types/hr'
import { getErrorMessage } from 'src/utils/http.util'
import { HrPageWrapper } from '../_components/hr-page-wrapper.component'
import { ContactFormModal } from './_components/contact-form-modal.component'
import { ContactSmsLogsModal } from './_components/contact-sms-logs-modal.component'
import { ContactsTable } from './_components/contacts-table.component'
import { OverrideChannelModal } from 'src/components/communication-channel/override-channel-modal.component'
import { useSession } from 'src/providers/auth.provider'

type TFormModalState = { mode: 'create' } | { mode: 'edit'; contact: THRContact } | null

export default function HrContactsPage() {
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const { notify } = useNotification()
  const queryClient = useQueryClient()
  const { currentUser } = useSession()

  const [formModalState, setFormModalState] = useState<TFormModalState>(null)
  const [contactToDelete, setContactToDelete] = useState<THRContact | null>(null)
  const [contactForSmsLogs, setContactForSmsLogs] = useState<THRContact | null>(null)
  const [contactForChannelOverride, setContactForChannelOverride] = useState<THRContact | null>(null)
  const [search, setSearch] = useState('')

  const {
    isLoading,
    error,
    data: contacts,
  } = useQuery({
    queryKey: ['hr-contacts', project.id],
    queryFn: () => HRContactService.getContacts(project.id),
  })

  const deleteMutation = useMutation({
    mutationFn: (contact: THRContact) => HRContactService.deleteContact(project.id, contact.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hr-contacts', project.id] })
      setContactToDelete(null)
    },
    onError: (error) => {
      notify({
        title: getErrorMessage(error),
        message: '',
        appearance: ECurecallNotificationAppearance.ERROR,
      })
      setContactToDelete(null)
    },
  })

  if (error) {
    return <ErrorQueryHandler error={error} />
  }

  return (
    <HrPageWrapper
      title={t('pages.hr.contacts.title')}
      actions={
        <div className="c-flex c-items-center c-gap-3">
          <div className="c-w-64">
            <CurecallSearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClear={() => setSearch('')}
              placeholder={t('pages.hr.contacts.table.search-placeholder')}
              searchInputAriaLabel={t('pages.hr.contacts.table.search-placeholder')}
              clearButtonAriaLabel={t('pages.hr.campaigns.new.contacts.filter-search-clear')}
            />
          </div>
          <CurecallButton
            appearance={ECurecallButtonAppearance.PRIMARY}
            size={ECurecallSize.SMALL}
            onClick={() => setFormModalState({ mode: 'create' })}
          >
            {t('pages.hr.contacts.add-button')}
          </CurecallButton>
        </div>
      }
    >
      {isLoading && <div className="loading" />}
      {contacts && contacts.length === 0 && (
        <p className="c-text-grey-500 c-mt-4">{t('pages.hr.contacts.empty')}</p>
      )}
      {contacts && contacts.length > 0 && (
        <ContactsTable
          contacts={contacts}
          search={search}
          onEdit={(contact) => setFormModalState({ mode: 'edit', contact })}
          onDelete={(contact) => setContactToDelete(contact)}
          onSmsLogs={(contact) => setContactForSmsLogs(contact)}
          onOverrideChannel={
            currentUser?.isAdmin ? (contact) => setContactForChannelOverride(contact) : undefined
          }
        />
      )}

      {formModalState && (
        <ContactFormModal
          contact={formModalState.mode === 'edit' ? formModalState.contact : undefined}
          onClose={() => setFormModalState(null)}
        />
      )}

      {contactForSmsLogs && (
        <ContactSmsLogsModal contact={contactForSmsLogs} onClose={() => setContactForSmsLogs(null)} />
      )}

      {contactForChannelOverride && (
        <OverrideChannelModal
          subjectType="HR_CONTACT"
          subjectId={contactForChannelOverride.id}
          onClose={() => setContactForChannelOverride(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['hr-contacts', project.id] })
          }}
        />
      )}

      <CurecallConfirmationModal
        isOpen={contactToDelete != null}
        color="ALERT"
        onConfirm={() => contactToDelete && deleteMutation.mutate(contactToDelete)}
        onCancel={() => setContactToDelete(null)}
        title={t('pages.hr.contacts.delete-confirm-title')}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('common.actions.delete')}
      >
        <span>{t('pages.hr.contacts.delete-confirm-description')}</span>
      </CurecallConfirmationModal>
    </HrPageWrapper>
  )
}
